import React, { useState, useEffect } from "react";
import Pagination from "../components/Pagination";
import BarcodeModal from "../components/BarcodeModal";
import {
  FaEdit,
  FaTrash,
  FaBarcode,
  FaPlusCircle,
  FaPills,
  FaSearch,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { useAtom } from "jotai";
import { clinicAtom, pharmacyAtom } from "../../_helper/constant/constant-atom";
import { useDeleteItem } from "../../_helper/actions/mutation/items";
import { toast } from "react-toastify";

const ItemMasterWrapper = () => {
  const navigate = useNavigate("");
  const [clinic] = useAtom(clinicAtom);
  const [pharamcy] = useAtom(pharmacyAtom);

  // Selected pharmacy state: if no clinic exists, default to the pharmacy from the atom.
  const [selectedPharmacy, setSelectedPharmacy] = useState(
    pharamcy?.id || null
  );

  // --- Fetch Pharmacies When Clinic Exists ---
  const clinicQuery = clinic?.id
    ? `?action=getPharmacy&clinic_id=${clinic?.id}`
    : null;
  const { data: fetchedPharmacies, isFetched } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    clinicQuery,
    !!clinic?.id
  );

  useEffect(() => {
    if (clinic?.id && isFetched && fetchedPharmacies?.length > 0) {
      setSelectedPharmacy(fetchedPharmacies[0]?.id);
    }
  }, [clinic?.id, isFetched, fetchedPharmacies]);
  // --- End Fetch Pharmacies ---

  // Determine the list of pharmacies for the dropdown.
  const pharmaciesList = clinic?.id
    ? fetchedPharmacies || []
    : pharamcy
      ? [pharamcy]
      : [];

  const [searchKeyword, setSearchKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [showAllBarcodes, setShowAllBarcodes] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);

  // Build the query string conditionally.
  const queryParams = clinic?.id
    ? `?action=getItem&clinic_id=${clinic.id}&page=${currentPage}&records_number=${itemsPerPage}&items_name=${searchKeyword}`
    : selectedPharmacy
      ? `?action=getItem&pharmacy_id=${selectedPharmacy}&page=${currentPage}&records_number=${itemsPerPage}&items_name=${searchKeyword}`
      : `?action=getItem&page=${currentPage}&records_number=${itemsPerPage}&items_name=${searchKeyword}`;

  const { data: allItems } = useItemsFilter(ApiEndPoint.INVENTORY, queryParams);

  useEffect(() => {
    if (allItems) {
      setFilteredItems(allItems);
    }
  }, [allItems]);

  const handleSearch = (keyword) => {
    setSearchKeyword(keyword);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleConfirmDelete = (item) => {
    setItemToDelete(item);
    setShowConfirmation(true);
  };

  const handleDeleteItem = () => {
    useDeleteItem.mutate(
      { data: { action: "delItem", id: itemToDelete.id } },
      {
        onSuccess: () => {
          toast.success("Item Deleted Successfully!");
          setShowConfirmation(false);
          setItemToDelete(null);
        },
      }
    );
  };

  const handleGenerateBarcode = (index) => {
    setSelectedItemIndex(index);
    setShowBarcodeModal(true);
    setShowAllBarcodes(false);
  };

  const handleGenerateAllBarcodes = () => {
    setShowAllBarcodes(true);
    setShowBarcodeModal(true);
  };

  const handleCloseBarcodeModal = () => {
    setShowBarcodeModal(false);
    setShowAllBarcodes(false);
    setSelectedItemIndex(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-extrabold text-gray-800">
            Item Management
          </h1>
          <p className="mt-2 text-gray-600">
            Easily manage and track your inventory items.
          </p>
        </div>

        {/* Top Bar: Buttons & Controls */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          {/* Left: Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate("/new/setup/item/new")}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow transition duration-200 flex items-center justify-center"
            >
              <FaPlusCircle className="mr-2" />
              Create New Item
            </button>
            <button
              onClick={handleGenerateAllBarcodes}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow transition duration-200 flex items-center justify-center"
            >
              <FaBarcode className="mr-2" />
              Generate All Barcodes
            </button>
          </div>

          {/* Right: Pharmacy & Search */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Pharmacy Dropdown */}
            <div className="flex items-center">
              <FaPills className="text-gray-500 mr-2" />
              <label
                htmlFor="pharmacy"
                className="mr-2 text-sm font-medium text-gray-700 whitespace-nowrap"
              >
                Pharmacy:
              </label>
              <select
                id="pharmacy"
                value={selectedPharmacy || ""}
                onChange={(e) => {
                  setSelectedPharmacy(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full max-w-xs p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Select Pharmacy --</option>
                {pharmaciesList.map((pharmacy) => (
                  <option key={pharmacy.id} value={pharmacy.id}>
                    {pharmacy.pharmacy_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Bar */}
            <div className="flex items-center">
              <FaSearch className="text-gray-500 mr-2" />
              <label
                htmlFor="search"
                className="mr-2 text-sm font-medium text-gray-700 whitespace-nowrap"
              >
                Search:
              </label>
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="ID, Name, Category..."
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Item List */}
        <div className="bg-white shadow-lg rounded-lg overflow-x-auto">
          {filteredItems?.length === 0 ? (
            <div className="p-6 text-gray-500 text-center">
              No items match your search criteria.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    S.NO
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Subcategory
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Is Medicine
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1 || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item?.items_name || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item?.item_category || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item?.item_subcategory || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item?.isMedicine ? "Yes" : "No" || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => navigate(`/new/setup/item/${item.id}`)}
                          className="flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition duration-200"
                        >
                          <FaEdit className="mr-1" /> Edit
                        </button>
                        <button
                          onClick={() => handleConfirmDelete(item)}
                          className="flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-md transition duration-200"
                        >
                          <FaTrash className="mr-1" /> Delete
                        </button>
                        <button
                          onClick={() => handleGenerateBarcode(index)}
                          className="flex items-center px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-md transition duration-200"
                        >
                          <FaBarcode className="mr-1" /> Barcode
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            totalItems={filteredItems.length}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </div>

      {/* Barcode Modal */}
      {showBarcodeModal && (
        <BarcodeModal
          items={
            showAllBarcodes ? filteredItems : [filteredItems[selectedItemIndex]]
          }
          onClose={handleCloseBarcodeModal}
        />
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 mx-4 transform transition-all">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Confirm Delete
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-bold">{itemToDelete?.name}</span>?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteItem}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemMasterWrapper;
